import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * Weight follows importance.
 *
 * Exactly one button on a screen is filled — the primary action. Everything else stays
 * close to invisible at rest and only resolves under the pointer: `outline` carries a
 * hairline and no fill, `ghost` carries nothing at all. The effect is that a toolbar
 * reads as a row of labels until you reach for it, which is what keeps a dense screen
 * calm without hiding any affordance.
 *
 * Icons inside the quiet variants are held back further (`[&_svg]:opacity-70`) and come
 * up to full strength on hover, so the glyph never out-weighs its own label.
 */
const buttonVariants = cva(
  [
    'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium',
    'transition-[color,box-shadow,background-color,border-color,opacity] duration-150',
    'outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35 focus-visible:border-ring',
    'disabled:pointer-events-none disabled:opacity-45',
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:transition-opacity",
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 active:bg-primary/95',
        destructive:
          'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/30',
        outline: [
          'border-border/80 text-foreground border bg-transparent',
          'hover:bg-accent hover:border-border-strong',
          '[&_svg]:opacity-70 hover:[&_svg]:opacity-100',
        ].join(' '),
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/75',
        ghost: [
          'text-muted-foreground hover:bg-accent hover:text-foreground',
          '[&_svg]:opacity-70 hover:[&_svg]:opacity-100',
        ].join(' '),
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-3.5 py-2 has-[>svg]:px-3',
        sm: 'h-8 gap-1.5 rounded-md px-2.5 text-[13px] has-[>svg]:px-2.5',
        lg: 'h-10 rounded-lg px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-sm': 'size-8 rounded-md',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ComponentProps<'button'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Renders a spinner and blocks interaction — pairs with a mutation's pending state. */
  loading?: boolean;
}

/**
 * forwardRef is required, not optional: every Radix trigger used with `asChild`
 * (tooltip, dropdown, popover, dialog) hands a ref down through Slot, and a plain
 * function component silently drops it — breaking positioning and focus return.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild = false, loading = false, disabled, children, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      ref={ref}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <>
          <Loader2 className="size-4 animate-spin" aria-hidden />
          {children}
        </>
      ) : (
        children
      )}
    </Comp>
  );
});

export { Button, buttonVariants };
